import { useCallback } from 'react';
import * as moment from 'moment';
import { GridApi, GridReadyEvent, FirstDataRenderedEvent, GridSizeChangedEvent, RowDataChangedEvent } from 'ag-grid-community';
import { IOrderRequest, IListItem } from '@nkr/models/index';

export const useOrderTrackingHandlers = (
    todoList: IOrderRequest[],
    filteredList: IOrderRequest[],
    kitTypes: IListItem[],
    shipVendors: IListItem[],
    gridApi: GridApi | null,
    onSetActiveTab: (tab: 'todo' | 'all') => void,
    onSetGridApi: (api: GridApi) => void,
    onGridReady: (e: GridReadyEvent) => void,
) => {
    const handleTabSelect = useCallback((tab: 'todo' | 'all') => {
        onSetActiveTab(tab);

        if (gridApi) {
            gridApi.setRowData(tab === 'all' ? filteredList : todoList);
            gridApi.sizeColumnsToFit();
        }
    }, [onSetActiveTab, gridApi, todoList, filteredList]);

    const handleGridReady = useCallback((e: GridReadyEvent) => {
        onGridReady(e);
        onSetGridApi(e.api);
        e.api.setRowData(todoList);
    }, [onGridReady, onSetGridApi, todoList]);

    const onGridSizeChanged = useCallback((e: GridSizeChangedEvent) => {
        const grid = document.getElementById('grid-wrapper');
        if (!grid) return;

        const gridWidth = grid.offsetWidth;
        const columnsToShow = [];
        const columnsToHide = [];
        let totalColsWidth = 0;

        const allColumns = e.columnApi.getAllColumns();

        for (const column of allColumns) {
            totalColsWidth += column.getMinWidth();
            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId());
            } else {
                columnsToShow.push(column.getColId());
            }
        }

        e.columnApi.setColumnsVisible(columnsToShow, true);
        e.columnApi.setColumnsVisible(columnsToHide, false);
        e.api.sizeColumnsToFit();
    }, []);

    const onDataFirstRendered = useCallback((e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }, []);

    const getKitTypes = (kitTypeId: number) => {
        return kitTypes.find(x => x.id === kitTypeId)?.description || 'N/A';
    };

    const getShipVendors = (vendorId: number) => {
        return shipVendors.find(x => x.id === vendorId)?.description || 'N/A';
    };

    const csvExport = useCallback(() => {
        if (!gridApi) return;

        const params = {
            columnGroups: true,
            fileName: `PhlebotRequests_${moment().format("YYYY_MM_DD")}.csv`,
            processCellCallback: (params: any) => {
                let result = params.value;
                let data = params.node.data;
                if (params.column.getColDef().field === "variance" && result === -1) {
                    return "N/A";
                }
                if (params.column.getColDef().field === "kitType") {
                    return getKitTypes(data.entityType);
                }
                if (params.column.getColDef().field === "shippingVendorId") {
                    return getShipVendors(data.shippingVendorId);
                }
                if (params.column.getColDef().field === "patientType") {
                    if (typeof result === 'object' || result == null)
                        return "--";
                    else
                        return result;
                }
                return result;
            }
        };

        gridApi.exportDataAsCsv(params);
    }, [gridApi, kitTypes, shipVendors]);

    const onRowDataChanged = useCallback((e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    }, []);

    return {
        handleTabSelect,
        handleGridReady,
        onGridSizeChanged,
        onDataFirstRendered,
        csvExport,
        onRowDataChanged
    };
};